<script>
/**
 * ****************************************************************************
 * @description     :   Button component
 * @created at      :   2024/08/01
 * @created by      :   ANS903 – phuongdt@ans-asia.com
 * @package         :   Move
 * @copyright       :   Copyright (c) A.N.S Asia
 * @version         :   1.0.0
 * ****************************************************************************
 */
/**
 * Import template
 */
import template from './template.html';

/**
 * Define content of button component
 */
var AnsTooltip = {
    template: template,
    props: {
        value: {
            type: String,
            default: ''
        },
        titleValue: {
            type: String,
            default: ''
        },
        classButton: {
            type: String,
            default: ''
        },
        id: {
            type: String,
            default: '0'
        },
        parentWidth: {
            type: [String, Number],
            default: 456
        },
        elementType: {
            type: String,
            default: 'span',
            validator(value) {
                return ['button', 'span', 'div'].includes(value);
            }
        },
        customTooltipClass: {
            type: String,
            default: ''
        },
        customStyle: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            showTooltip: false,
            resizeObserver: null,
            resizeRafId: null,
            lastParentWidth: null
        };
    },
    mounted() {
        this.registerResizeObserver();
        window.addEventListener('resize', this.checkShowTooltipTable);
        this.checkShowTooltipTable();
    },
    unmounted() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
        if (this.resizeRafId) {
            cancelAnimationFrame(this.resizeRafId);
            this.resizeRafId = null;
        }
        window.removeEventListener('resize', this.checkShowTooltipTable);
    },
    watch: {
        value: {
            handler: 'checkShowTooltipTable',
            immediate: true
        },
        titleValue: {
            handler: 'checkShowTooltipTable',
            immediate: true
        },
        parentWidth: {
            handler: 'checkShowTooltipTable',
            immediate: true
        }
    },
    computed: {
        renderTitle() {
            return this.showTooltip ? this.value : '';
        }
    },
    methods: {
        registerResizeObserver() {
            this.$nextTick(() => {
                const tooltipElement = this.$refs.tooltip;
                if (!tooltipElement || typeof ResizeObserver === 'undefined') return;
                const parentElement = tooltipElement.parentElement;
                if (!parentElement) return;
                if (this.resizeObserver) {
                    this.resizeObserver.disconnect();
                }
                this.resizeObserver = new ResizeObserver(() => {
                    const parentWidth = parentElement.getBoundingClientRect().width;
                    if (this.lastParentWidth === parentWidth) return;
                    this.lastParentWidth = parentWidth;
                    if (this.resizeRafId) {
                        cancelAnimationFrame(this.resizeRafId);
                    }
                    this.resizeRafId = requestAnimationFrame(() => {
                        this.checkShowTooltipTable();
                    });
                });
                this.resizeObserver.observe(parentElement);
            });
        },
        checkShowTooltipTable() {
            this.$nextTick(() => {
                const tooltipElement = this.$refs.tooltip;
                if (!tooltipElement) return;

                tooltipElement.style.width = 'auto';
                tooltipElement.style.display = 'inline-block';
                tooltipElement.style.whiteSpace = 'nowrap';

                const realWidth = tooltipElement.offsetWidth;
                const hasParentWidth = this.parentWidth !== null && this.parentWidth !== undefined && String(this.parentWidth) !== '';
                const isPercentage = hasParentWidth && String(this.parentWidth).includes('%');
                const parentElement = tooltipElement.parentElement;
                const parentElementWidth = parentElement ? parentElement.offsetWidth : null;
                const fallbackWidth = parentElementWidth ?? realWidth;

                if (hasParentWidth) {
                    if (isPercentage) {
                        tooltipElement.style.width = `${this.parentWidth}`;
                        this.showTooltip = true;
                    } else {
                        let numericWidth = parseFloat(this.parentWidth);
                        if (!numericWidth || Number.isNaN(numericWidth)) {
                            numericWidth = fallbackWidth;
                        }
                        const effectiveWidth = parentElementWidth && numericWidth > parentElementWidth ? parentElementWidth : numericWidth;
                        tooltipElement.style.width = `${effectiveWidth}px`;
                        this.showTooltip = realWidth > effectiveWidth;
                    }
                } else {
                    tooltipElement.style.width = `${fallbackWidth}px`;
                    this.showTooltip = realWidth > fallbackWidth;
                }
                if (this.titleValue) {
                    this.showTooltip = true;
                }
                if (this.showTooltip) {
                    tooltipElement.style.textOverflow = 'ellipsis';
                    tooltipElement.style.overflow = 'hidden';
                    tooltipElement.style.whiteSpace = 'nowrap';
                }
            });
        }
    }
};
export default AnsTooltip;
</script>
